// A small swatch of plain jersey with two wedges of short rows, for the shaping section: its
// knitmap row by row, and where each loop lies once the swatch is off the needles and has relaxed.
//
// A wedge is short rows in pairs: the carriage knits from the left edge part of the way across,
// turns, and knits back to the edge, each pair turning two needles sooner than the last. The
// needles it skips hold their loops, so the left side gains rows the right side doesn't; off the
// needles, the left side is the longer and the swatch bends round the right.

export const SWATCH_NEEDLES = 11;

const FULL = [0, SWATCH_NEEDLES - 1] as const;
const WEDGE = [9, 7, 5, 3].flatMap((turn) => [[0, turn] as const, [0, turn] as const]);

// Each row's first and last needle; even rows run left to right, odd rows back.
export const SWATCH_ROWS: readonly (readonly [number, number])[] = [
  FULL,
  FULL,
  FULL,
  FULL,
  ...WEDGE,
  FULL,
  FULL,
  ...WEDGE,
  FULL,
  FULL,
];

// Where the steps start: full rows, the first short rows, the rest of the wedge, and the full rows
// that close it, with the second wedge after them.
export const SWATCH_STEP_ROWS = [0, 4, 6, 12] as const;

// A loop's width, and the height of a course.
export const LOOP_WIDTH = 1;
export const COURSE_HEIGHT = 0.62;

export type SwatchLoop = {
  readonly row: number;
  readonly needle: number;
  // The loop this one was pulled through, on the same needle in an earlier row; -1 on the first row.
  readonly below: number;
};

export type Swatch = {
  readonly loops: readonly SwatchLoop[];
  // Each row's loops in the order they were knitted.
  readonly rows: readonly (readonly number[])[];
  // Where each loop sits once the swatch has relaxed: x and y in turn, the first row level along
  // the bottom.
  readonly at: Float64Array;
  readonly bounds: { readonly left: number; readonly right: number; readonly bottom: number; readonly top: number };
};

export const rowDirection = (row: number): 1 | -1 => (row % 2 === 0 ? 1 : -1);

const buildLoops = () => {
  const loops: SwatchLoop[] = [];
  const rows: number[][] = [];
  const newest = new Array<number>(SWATCH_NEEDLES).fill(-1);
  SWATCH_ROWS.forEach(([first, last], row) => {
    const needles = Array.from({ length: last - first + 1 }, (_, index) => first + index);
    if (rowDirection(row) === -1) needles.reverse();
    rows.push(
      needles.map((needle) => {
        loops.push({ row, needle, below: newest[needle] });
        newest[needle] = loops.length - 1;
        return loops.length - 1;
      }),
    );
  });
  return { loops, rows };
};

// Springs between neighbouring loops, each pulled toward its length in turn until the swatch
// settles: along each row and each wale, across each square of four loops (none spans a short
// row's turn, where the fabric is free to open), and, more softly, over two loops so it lies smooth.
const relax = (loops: readonly SwatchLoop[], rows: readonly (readonly number[])[]) => {
  const springs: [number, number, number, number][] = [];
  const diagonal = Math.hypot(LOOP_WIDTH, COURSE_HEIGHT);
  const inRow = (row: readonly number[]) => [...row].sort((a, b) => loops[a].needle - loops[b].needle);
  rows.forEach((row) => {
    const sorted = inRow(row);
    sorted.forEach((loop, index) => {
      const next = sorted[index + 1];
      const below = loops[loop].below;
      if (below >= 0) springs.push([loop, below, COURSE_HEIGHT, 1]);
      if (below >= 0 && loops[below].below >= 0) springs.push([loop, loops[below].below, 2 * COURSE_HEIGHT, 0.3]);
      if (next === undefined) return;
      springs.push([loop, next, LOOP_WIDTH, 1]);
      const after = sorted[index + 2];
      if (after !== undefined) springs.push([loop, after, 2 * LOOP_WIDTH, 0.3]);
      const nextBelow = loops[next].below;
      if (below >= 0 && nextBelow >= 0 && loops[below].row === loops[nextBelow].row) {
        springs.push([loop, nextBelow, diagonal, 0.6], [next, below, diagonal, 0.6]);
      }
    });
  });

  // From the knitmap's layout: every row level, every needle a loop's width apart.
  const at = new Float64Array(loops.length * 2);
  loops.forEach(({ row, needle }, index) => {
    at[index * 2] = needle * LOOP_WIDTH;
    at[index * 2 + 1] = row * COURSE_HEIGHT;
  });
  for (let pass = 0; pass < 900; pass += 1) {
    for (const [a, b, length, stiffness] of springs) {
      const dx = at[b * 2] - at[a * 2];
      const dy = at[b * 2 + 1] - at[a * 2 + 1];
      const distance = Math.hypot(dx, dy) || 1e-9;
      const pull = (0.5 * stiffness * (distance - length)) / distance;
      at[a * 2] += dx * pull;
      at[a * 2 + 1] += dy * pull;
      at[b * 2] -= dx * pull;
      at[b * 2 + 1] -= dy * pull;
    }
  }

  // Level the first row along the bottom, its middle at x = 0.
  const first = inRow(rows[0]);
  const start = first[0];
  const end = first[first.length - 1];
  const angle = Math.atan2(at[end * 2 + 1] - at[start * 2 + 1], at[end * 2] - at[start * 2]);
  const middleX = (at[start * 2] + at[end * 2]) / 2;
  const middleY = (at[start * 2 + 1] + at[end * 2 + 1]) / 2;
  const cos = Math.cos(-angle);
  const sin = Math.sin(-angle);
  const bounds = { left: Infinity, right: -Infinity, bottom: Infinity, top: -Infinity };
  for (let index = 0; index < loops.length; index += 1) {
    const x = at[index * 2] - middleX;
    const y = at[index * 2 + 1] - middleY;
    at[index * 2] = x * cos - y * sin;
    at[index * 2 + 1] = x * sin + y * cos;
    bounds.left = Math.min(bounds.left, at[index * 2]);
    bounds.right = Math.max(bounds.right, at[index * 2]);
    bounds.bottom = Math.min(bounds.bottom, at[index * 2 + 1]);
    bounds.top = Math.max(bounds.top, at[index * 2 + 1]);
  }
  return { at, bounds };
};

let swatch: Swatch | null = null;

// Built the first time it is asked for: a few thousand spring steps.
export const getSwatch = (): Swatch => {
  if (!swatch) {
    const { loops, rows } = buildLoops();
    swatch = { loops, rows, ...relax(loops, rows) };
  }
  return swatch;
};

// The newest loop on each needle before a row: the loops a short row leaves holding.
export const heldBefore = ({ loops, rows }: Swatch, row: number): readonly number[] => {
  const newest = new Array<number>(SWATCH_NEEDLES).fill(-1);
  for (let earlier = 0; earlier < row; earlier += 1) {
    for (const loop of rows[earlier]) newest[loops[loop].needle] = loop;
  }
  return newest;
};
